import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChalkboardUser, faCircleCheck, faCircleDollarToSlot, faCircleXmark, faClipboardCheck, faClipboardQuestion, faDoorOpen, faUsers } from "@fortawesome/free-solid-svg-icons";
import { faCircle, faClock } from "@fortawesome/free-regular-svg-icons";

import { nombreDeAlumno } from "../../../../utils/nombreCorto";
import { AlumnoClase, Clase, alumnosDe } from "./calendarioApi";
import { GlobitoApi } from "./globito";
import { DIAS, bajoMinimo, dinero, horaClase, horaInicio, iconoCurso, maestraDe, sinPasar, subClase, tituloClase } from "./formato";

export interface Contexto {
	campusId: number;
	countryId: number | null;
	globito: GlobitoApi;
	/** Abre el panel lateral de la clase. */
	onAbrir: (c: Clase) => void;
}

// Un horario puede tener dos sesiones el mismo día: la hora de la sesión va en la llave.
export const llaveClase = (c: Clase) => `${c.horarioId}|${c.fecha}|${c.inicio}`;
export const estadoDeCuenta = (alumnoId: number, campusId: number) => `/statement?alumno=${alumnoId}&plantel=${campusId}`;
const meses = (n: number) => (n === 1 ? "1 mes" : `${n} meses`);

export const IconoCurso: React.FC<{ c: Clase; chico?: boolean }> = ({ c, chico }) => (
	<span className={"calcIco" + (chico ? " chico" : "")} aria-hidden="true"><FontAwesomeIcon icon={iconoCurso(c)} /></span>
);

/** La lista de la clase, pedida una sola vez para globitos y panel. */
export const useAlumnos = (campusId: number, c: Clase | null): { alumnos: AlumnoClase[] | null; error: boolean } => {
	const [alumnos, setAlumnos] = useState<AlumnoClase[] | null>(null);
	const [error, setError] = useState(false);
	useEffect(() => {
		setAlumnos(null); setError(false);
		if (!c) return;
		let vigente = true;
		alumnosDe(campusId, c.horarioId, c.fecha).then((a) => { if (vigente) setAlumnos(a); }).catch(() => { if (vigente) setError(true); });
		return () => { vigente = false; };
	}, [campusId, c]);
	return { alumnos, error };
};

const Cargando: React.FC<{ error: boolean }> = ({ error }) => <p className="calcGloboVacio">{error ? "No se pudo cargar la lista." : "Cargando…"}</p>;

const Deudores: React.FC<{ c: Clase; ctx: Contexto; limite?: number }> = ({ c, ctx, limite }) => {
	const { alumnos, error } = useAlumnos(ctx.campusId, c);
	if (!alumnos) return <Cargando error={error} />;
	const deben = alumnos.filter((a) => a.vencido > 0).sort((a, b) => b.vencido - a.vencido);
	const ver = limite ? deben.slice(0, limite) : deben;
	return (
		<>
			{ver.map((a) => (
				<div className="calcGloboFila" key={a.matriculaId}>
					<Link to={estadoDeCuenta(a.id, ctx.campusId)} title="Abrir Estado de Cuenta">{nombreDeAlumno(a.nombre, a.apellido)}</Link>
					<span className="calcMonto">{dinero(a.vencido, ctx.countryId)}<small>{meses(a.meses)}</small></span>
				</div>
			))}
			{deben.length > ver.length ? <p className="calcGloboVacio">y {deben.length - ver.length} más</p> : null}
		</>
	);
};

export const GloboSaldo: React.FC<{ c: Clase; ctx: Contexto }> = ({ c, ctx }) => (
	<>
		<h4 className="calcGloboTit">Saldo Vencido <small>{c.vencido!.alumnos === 1 ? "1 alumna" : `${c.vencido!.alumnos} alumnos`}</small></h4>
		<Deudores c={c} ctx={ctx} />
	</>
);

export const GloboAsistencia: React.FC<{ c: Clase; ctx: Contexto; pasarLista: boolean }> = ({ c, ctx, pasarLista }) => {
	const { alumnos, error } = useAlumnos(ctx.campusId, c);
	if (c.lista && !c.lista.pasada) {
		return (
			<>
				<h4 className="calcGloboTit">Lista Sin Pasar <small>{c.activos} alumnos</small></h4>
				{pasarLista ? <div className="calcGloboPie"><Link className="btn btn-primary btn-sm rounded-pill" to={"/attendances?horario=" + c.horarioId}><FontAwesomeIcon icon={faClipboardCheck} />&nbsp;Pasar Lista</Link></div> : null}
			</>
		);
	}
	const grupo = (titulo: string, xs: AlumnoClase[]) => xs.length < 1 ? null : (
		<>
			<div className="calcGloboSec">{titulo} · {xs.length}</div>
			{xs.map((a) => <div className="calcGloboFila conIcono" key={a.matriculaId}><IconoAsistencia a={a} conLista /><span className={a.asistencia === false ? "calcOro" : ""}>{nombreDeAlumno(a.nombre, a.apellido)}</span></div>)}
		</>
	);
	return (
		<>
			<h4 className="calcGloboTit">Asistencia <small>{c.lista?.presentes ?? 0} de {c.activos}</small></h4>
			{!alumnos ? <Cargando error={error} /> : (
				<>
					{grupo("Ausentes", alumnos.filter((a) => a.asistencia === false))}
					{grupo("Sin Marcar", alumnos.filter((a) => a.asistencia === null))}
					{grupo("Presentes", alumnos.filter((a) => a.asistencia === true))}
				</>
			)}
		</>
	);
};

/** Los tres datos de abajo de la tarjeta: alumnos, asistencia y saldo vencido (estos dos abren su globito). */
export const Datos: React.FC<{ c: Clase; ctx: Contexto; pasarLista: boolean }> = ({ c, ctx, pasarLista }) => {
	const g = ctx.globito;
	const bajo = bajoMinimo(c);
	return (
		<div className="calcDatos">
			<span className={"calcDato" + (bajo ? " calcOro" : "")} title={bajo ? `Bajo el mínimo de ${c.minimo}` : "Alumnos / cupo del aula"}>
				<FontAwesomeIcon icon={faUsers} />{c.activos}{c.aula?.cupo ? `/${c.aula.cupo}` : ""}
			</span>
			{c.activos > 0 && c.lista ? (
				c.lista.pasada ? (
					<button type="button" className={"calcDato boton" + (c.lista.presentes < c.activos * 0.75 ? " calcOro" : "")} aria-label="Asistencia"
						{...g.disparador("asis" + llaveClase(c), () => <GloboAsistencia c={c} ctx={ctx} pasarLista={pasarLista} />)}>
						<FontAwesomeIcon icon={faClipboardCheck} />{c.lista.presentes}/{c.activos}
					</button>
				) : (
					<button type="button" className="calcDato boton calcOro" aria-label="Lista sin pasar"
						{...g.disparador("asis" + llaveClase(c), () => <GloboAsistencia c={c} ctx={ctx} pasarLista={pasarLista} />)}>
						<FontAwesomeIcon icon={faClipboardQuestion} />
					</button>
				)
			) : null}
			{c.vencido && c.vencido.alumnos > 0 ? (
				<button type="button" className="calcDato boton calcVencido" aria-label="Saldo vencido"
					{...g.disparador("saldo" + llaveClase(c), () => <GloboSaldo c={c} ctx={ctx} />)}>
					<FontAwesomeIcon icon={faCircleDollarToSlot} />{dinero(c.vencido.total, ctx.countryId)}
				</button>
			) : null}
		</div>
	);
};

export const Tarjeta: React.FC<{ c: Clase; ctx: Contexto; elegida: boolean; pasarLista: boolean }> = ({ c, ctx, elegida, pasarLista }) => {
	const maestra = maestraDe(c);
	return (
		<article className={"calcTarjeta" + (elegida ? " elegida" : "")} tabIndex={0} onClick={() => ctx.onAbrir(c)}
			onKeyDown={(e) => { if (e.key === "Enter") ctx.onAbrir(c); }}>
			<div className="calcTarjetaCab">
				<IconoCurso c={c} />
				<div className="calcTitulo"><b>{tituloClase(c)}</b><span>{subClase(c)}</span></div>
			</div>
			<div className="calcDetalle">
				<div><FontAwesomeIcon icon={faClock} />{horaClase(c)}</div>
				<div className={maestra ? "" : "calcOro"}><FontAwesomeIcon icon={faChalkboardUser} />{maestra || "Sin Maestra"}</div>
				<div><FontAwesomeIcon icon={faDoorOpen} />{[c.aula?.nombre, c.edificio?.nombre].filter(Boolean).join(" · ") || "Sin Aula"}</div>
			</div>
			<Datos c={c} ctx={ctx} pasarLista={pasarLista} />
		</article>
	);
};

/** Tarjetita que sale con el ratón encima de una clase en la Semana y en Ocupación. */
export const GloboClase: React.FC<{ c: Clase; ctx: Contexto }> = ({ c, ctx }) => {
	const maestra = maestraDe(c);
	const bajo = bajoMinimo(c);
	return (
		<>
			<div className="calcGloboCab"><IconoCurso c={c} chico /><div><b>{tituloClase(c)}</b><span>{subClase(c)}</span></div></div>
			<div className="calcDetalle calcGloboDet">
				<div><FontAwesomeIcon icon={faClock} />{DIAS[c.dia]} · {horaClase(c)}</div>
				<div className={maestra ? "" : "calcOro"}><FontAwesomeIcon icon={faChalkboardUser} />{maestra || "Sin Maestra"}</div>
				<div><FontAwesomeIcon icon={faDoorOpen} />{[c.aula?.nombre, c.edificio?.nombre].filter(Boolean).join(" · ") || "Sin Aula"}</div>
				<div className={bajo ? "calcOro" : ""}><FontAwesomeIcon icon={faUsers} />{c.activos}{c.aula?.cupo ? ` de ${c.aula.cupo}` : ""}{bajo ? ` · mínimo ${c.minimo}` : ""}</div>
				{c.lista && c.activos > 0 ? (
					c.lista.pasada
						? <div><FontAwesomeIcon icon={faClipboardCheck} />{c.lista.presentes} de {c.activos}</div>
						: <div className="calcOro"><FontAwesomeIcon icon={faClipboardQuestion} />Lista sin pasar</div>
				) : null}
			</div>
			{c.vencido && c.vencido.alumnos > 0 ? (
				<>
					<div className="calcGloboSec">Saldo Vencido · {dinero(c.vencido.total, ctx.countryId)}</div>
					<Deudores c={c} ctx={ctx} limite={5} />
				</>
			) : null}
		</>
	);
};

/** Una clase en la cuadrícula de la Semana. */
export const ChipSemana: React.FC<{ c: Clase; ctx: Contexto }> = ({ c, ctx }) => {
	const d = ctx.globito.disparador("clase" + llaveClase(c), () => <GloboClase c={c} ctx={ctx} />);
	return (
		<button type="button" className="calcChip" onMouseEnter={d.onMouseEnter} onMouseLeave={d.onMouseLeave}
			onClick={() => { ctx.globito.cerrar(); ctx.onAbrir(c); }}>
			<span className="calcChipArriba"><IconoCurso c={c} chico /><span className="calcChipNombre">{tituloClase(c)}</span></span>
			<span className="calcChipAbajo">
				<span>{horaInicio(c)}</span>
				{c.vencido && c.vencido.alumnos > 0 ? <FontAwesomeIcon icon={faCircleDollarToSlot} className="calcVencidoTxt" title="Saldo vencido" /> : null}
				{sinPasar(c) ? <FontAwesomeIcon icon={faClipboardQuestion} className="calcOro" title="Lista sin pasar" /> : null}
				<span className="calcChipCuenta">{c.activos}{c.aula?.cupo ? `/${c.aula.cupo}` : ""}</span>
			</span>
		</button>
	);
};

/* Presente en verde (Esteban, 2026-11-02: «deberían verse en verde los que asisten; en gris es bien confuso»), ausente en oro con X, sin marcar vacío. */
export const IconoAsistencia: React.FC<{ a: AlumnoClase; conLista: boolean }> = ({ a, conLista }) => {
	if (!conLista || a.asistencia === null) return <FontAwesomeIcon icon={faCircle} className="calcAsis sin" title="Sin marcar" />;
	if (a.asistencia) return <FontAwesomeIcon icon={faCircleCheck} className="calcAsis si" title="Presente" />;
	return <FontAwesomeIcon icon={faCircleXmark} className="calcAsis no" title="Ausente" />;
};

